import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  type ElementRef,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ButtonComponent } from '../ui/button/button.component';

const PASSPHRASE_HASH = 'f910f44bee3809085a17b21c652e6f74c67c93bcfaf220a39fc24ae5c40c9751';

@Component({
  selector: 'orange-gate',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: var(--grey-100);
      }

      .gate-card {
        width: 100%;
        max-width: 400px;
        margin: var(--space-3);
        padding: var(--space-5) var(--space-4);
        background: var(--surface);
        border: var(--border-hairline) solid var(--grey-400);
        border-radius: var(--radius-none);
        text-align: center;
      }

      .logo {
        font-size: 34px;
        line-height: 36px;
        font-weight: 700;
        color: var(--ink);
        margin-bottom: var(--space-4);
      }

      .title {
        font-size: 20px;
        line-height: 24px;
        font-weight: 700;
        margin-bottom: var(--space-4);
      }

      .field {
        width: 100%;
        margin-bottom: var(--space-3);
      }

      .field input {
        width: 100%;
        padding: var(--space-2) var(--space-3);
        min-height: 44px;
        font-size: 16px;
        line-height: 24px;
        font-weight: 400;
        border: var(--border-control) solid var(--ink);
        border-radius: var(--radius-none);
        background: var(--surface);
        color: var(--ink);
      }

      .error {
        color: var(--ink);
        font-size: 14px;
        line-height: 20px;
        font-weight: 700;
        margin-bottom: var(--space-3);
        padding-left: var(--space-3);
        border-left: 3px solid var(--danger);
        text-align: left;
      }
    `,
  ],
  template: `
    <div class="gate-card">
      <div class="logo" aria-hidden="true">orange</div>
      <h1 class="title">Acces demonstrație</h1>

      <form (submit)="onSubmit($event)">
        <div class="field">
          <input
            #passInput
            type="password"
            autocomplete="off"
            placeholder="Parolă"
            [attr.aria-label]="'Parolă de acces'"
            [attr.aria-invalid]="error() ? 'true' : null"
            [attr.aria-describedby]="error() ? 'gate-error' : null"
            (input)="passphrase.set(passInput.value)"
          />
        </div>

        @if (error()) {
          <p class="error" id="gate-error" role="alert">Parolă incorectă</p>
        }

        <orange-button
          variant="primary"
          size="block"
          type="submit"
          [busy]="checking()"
        >
          Accesează
        </orange-button>
      </form>
    </div>
  `,
})
export class GateComponent implements AfterViewInit {
  readonly granted = output<void>();

  readonly passphrase = signal('');
  readonly error = signal(false);
  readonly checking = signal(false);

  private readonly inputRef = viewChild.required<ElementRef<HTMLInputElement>>('passInput');

  ngAfterViewInit(): void {
    this.inputRef().nativeElement.focus();
  }

  async onSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const value = this.passphrase().trim();
    if (!value) return;

    this.checking.set(true);
    this.error.set(false);

    const digest = await sha256Hex(value);

    if (digest === PASSPHRASE_HASH) {
      this.granted.emit();
    } else {
      this.error.set(true);
      this.checking.set(false);
    }
  }
}

async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const buffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
